import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { concat, find } from 'lodash'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import ProductCard from '@/components/base/product-card'
import Section from '@/components/base/section'
import FilterList from '@/components/sections/filter-list'
import LoadMore from '@/components/sections/load-more'
import Spacer from '@/components/sections/spacer'
import { normalizeCollectionData } from '@/utils/page-data'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import {
  gridPostTypesFiltersRevealAnimation,
  gridPostTypesRevealAnimation
} from './animations'
import { WPHeaders, WPTaxonomy } from '@/types'

type GridPostTypesProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    post_type: string
    component_type: string
    collection: any[]
    filter: string
    headers: WPHeaders
    per_page: string
    filter_terms: WPTaxonomy[]
    use_filter: boolean
    filter_type: 'static' | 'dynamic'
    static_filter: WPTaxonomy
    static_filter_parent: 'ancestor' | 'children'
    use_pagination: boolean
    show_link: boolean
    className: string
  }>

const GridPostTypes = ({
  post_type,
  collection: posts,
  className,
  use_filter,
  filter_type,
  static_filter,
  static_filter_parent,
  filter_terms,
  headers: postHeaders,
  use_pagination,
  show_link,
  per_page
}: GridPostTypesProps) => {
  const [headers, setHeaders] = useState<WPHeaders>(postHeaders!)
  const [currentFilter, setCurrentFilter] = useState<WPTaxonomy>()
  const [collection, setCollection] = useState<any[]>(
    normalizeCollectionData(posts || [], show_link || false)
  )
  const [pagination, setPagination] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const [needsPagination, setNeedsPagination] = useState(
    headers['X-WP-TotalPages'] > 1
  )
  const previousFilter = useRef<WPTaxonomy>()
  // const { updateLayout } = useLayout()

  const isStaticChildren = useMemo(
    () =>
      use_filter &&
      filter_type === 'static' &&
      static_filter_parent === 'children',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const getParams = useCallback(
    (page: number, filter?: WPTaxonomy) => {
      const perPage = use_pagination ? per_page : 1000
      const params = {
        per_page: perPage,
        page
      } as any

      if (use_filter && filter) {
        if (post_type === 'post') {
          params['categories'] = filter?.term_id || ''
        } else if (post_type?.includes('category')) {
          params.parent = filter?.term_id || ''
        } else {
          params[`filter[${filter?.taxonomy || ''}]`] = filter?.slug || ''
        }
      }

      return {
        params
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [post_type, use_pagination, per_page]
  )

  const getPosts = useCallback(
    async (page: number, filter?: WPTaxonomy) => {
      const postType = post_type == 'post' ? 'posts' : post_type
      const needsReset =
        filter_type === 'dynamic'
          ? filter?.slug !== previousFilter.current?.slug
          : false

      setFetching(true)

      const { data, headers } = await getPromiseFromFetch({
        method: 'get',
        url: isStaticChildren
          ? `${API.BASE}/childterms/${postType}`
          : `${API.BASE}/${postType}`,
        params: getParams(
          page,
          filter_type === 'dynamic' ? filter : static_filter
        )
      })

      setHeaders({
        'X-WP-Total': parseInt(headers['x-wp-total']),
        'X-WP-TotalPages': parseInt(headers['x-wp-totalpages'])
      })

      setCollection((previousCollection) => {
        const collection = normalizeCollectionData(data, show_link || false)
        return needsReset ? collection : concat(previousCollection, collection)
      })

      setNeedsPagination(page < parseInt(headers['x-wp-totalpages']))

      setFetching(false)

      requestAnimationFrame(() => {
        ScrollTrigger.refresh()
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [post_type, getParams]
  )

  useEffect(() => {
    if (pagination < 0) return
    getPosts(pagination, currentFilter)
  }, [pagination, currentFilter, getPosts])

  return (
    <>
      {use_filter && filter_type === 'dynamic' ? (
        <>
          <Motion animation={gridPostTypesFiltersRevealAnimation}>
            {(motionRef) => (
              <FilterList
                ref={motionRef}
                fetching={fetching}
                terms={filter_terms}
                onFilterChange={(term) => {
                  setPagination(1)
                  setCurrentFilter((oldFilter) => {
                    previousFilter.current = oldFilter
                    return find(filter_terms, {
                      term_id: term?.term_id
                    })
                  })
                }}
              />
            )}
          </Motion>
          <Spacer variant="sm" />
        </>
      ) : null}

      <Motion animation={gridPostTypesRevealAnimation}>
        {(motionRef) => (
          <Section ref={motionRef}>
            <Container>
              <Grid className="items-center gap-y-spacing-lg">
                <div className="col-span-2 md:col-span-6 lg:col-span-12">
                  <div className="grid grid-cols-2 gap-spacing-xs md:grid-cols-3 lg:grid-cols-4">
                    {collection?.map((item, index) => (
                      <div
                        key={`product-card-${index}`}
                        className="col-span-2 md:col-span-1"
                      >
                        <ProductCard {...item} data-motion-card />
                      </div>
                    ))}
                  </div>
                </div>
              </Grid>
            </Container>
          </Section>
        )}
      </Motion>

      {use_pagination && needsPagination ? (
        <>
          <Spacer variant="md" />
          <LoadMore
            className={className}
            fetching={fetching}
            onClick={() => {
              setPagination((pagination) => {
                previousFilter.current = currentFilter
                return pagination < 0 ? 2 : pagination + 1
              })
            }}
          />
        </>
      ) : null}
    </>
  )
}

export default GridPostTypes
